import React from 'react';
import { EventLogClinicDaily, EventLogClinicSummary } from '../../services/eventLog.service';
import LineChart from './charts/LineChart';
import { computeTrend, formatTrend, trendArrow, trendColor } from './utils/trend';

type Props = {
  summary?: EventLogClinicSummary;
  daily?: EventLogClinicDaily;
  loading?: boolean;
  filteredUserName?: string;
};

const formatJoined = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const StatusPill: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return null;
  const s = status.toLowerCase();
  const color =
    s === 'active'
      ? 'bg-green-100 text-green-700'
      : s === 'canceled' || s === 'terminated' || s === 'expired'
        ? 'bg-red-100 text-red-700'
        : 'bg-gray-100 text-gray-700';

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
};

type KpiProps = {
  label: string;
  value: number;
  previous: number;
  suffix?: string;
  isInverse?: boolean;
};

const Kpi: React.FC<KpiProps> = ({ label, value, previous, suffix, isInverse }) => {
  const trend = computeTrend(value, previous);

  return (
    <div className="flex flex-col">
      <span className="text-3xl font-semibold text-gray-900 tabular-nums">
        {value.toLocaleString()}
        {suffix && <span className="text-base font-normal text-gray-500 ml-1">{suffix}</span>}
      </span>
      <span className="text-xs text-gray-500 mt-0.5">{label}</span>
      <span className={`text-xs mt-1 ${trendColor(trend.direction, isInverse)}`}>
        {trendArrow(trend.direction)} {formatTrend(trend)}
      </span>
    </div>
  );
};

const SkeletonKpi: React.FC = () => (
  <div className="flex flex-col gap-2">
    <div className="h-9 w-20 rounded bg-gray-200 animate-pulse" />
    <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
    <div className="h-3 w-12 rounded bg-gray-100 animate-pulse" />
  </div>
);

const SummaryCard: React.FC<Props> = ({ summary, daily, loading, filteredUserName }) => {
  const clinic = summary?.clinic;
  const kpis = summary?.kpis;
  const prev = summary?.previousKpis;
  const dailyData = daily?.daily ?? [];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-5">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>
            Plan: <span className="text-gray-700 font-medium">{clinic?.plan || '—'}</span>
          </span>
          <span className="text-gray-300">·</span>
          <StatusPill status={clinic?.status} />
          <span className="text-gray-300">·</span>
          <span>
            Joined:{' '}
            <span className="text-gray-700 font-medium">
              {formatJoined(clinic?.subscriptionStartDate)}
            </span>
          </span>
        </div>
        {filteredUserName && (
          <div className="ml-auto text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 font-medium">
              Filtered to {filteredUserName}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
        {loading && !summary ? (
          <>
            <SkeletonKpi />
            <SkeletonKpi />
            <SkeletonKpi />
            <SkeletonKpi />
          </>
        ) : (
          <>
            <Kpi label="Events" value={kpis?.totalEvents ?? 0} previous={prev?.totalEvents ?? 0} />
            <Kpi
              label="Active Users"
              value={kpis?.activeUsers ?? 0}
              previous={prev?.activeUsers ?? 0}
            />
            <Kpi label="Sessions" value={kpis?.sessions ?? 0} previous={prev?.sessions ?? 0} />
            <Kpi
              label="Days Active"
              value={kpis?.daysActive ?? 0}
              previous={prev?.daysActive ?? 0}
              suffix={`/ ${kpis?.rangeDays ?? 0}`}
            />
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        {loading && !daily ? (
          <div className="h-40 rounded bg-gray-50 animate-pulse" />
        ) : (
          <LineChart data={dailyData} height={160} />
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
